/**
 * IDS (InvoiceDataSource) 状态注册表 — Single Source of Truth
 *
 * ▸ 新增 Pipeline 阶段时，只需在此注册表添加一行即可。
 * ▸ 所有下游常量（枚举值、标签、颜色、分类数组、步骤映射）均自动派生。
 * ▸ 任何组件都不应硬编码 IDS 状态字符串，一律从此文件 import。
 *
 * 字段说明：
 *   category:  'initial' | 'in_progress' | 'completed' | 'failed' | 'blocked' | 'terminal'
 *   label:     中文显示文案
 *   color:     Ant Design Tag 颜色
 *   step:      步骤条序号（用于 StepBar 显示）
 *   pollingMessage: （仅 in_progress）轮询提示文案
 *   validationAvailable: 该状态下是否可加载 Validation 结果
 */
export const IDS_STATUS_REGISTRY = {
    // --- 初始 ---
    RECEIVED:                       { category: "initial",     label: "已接收",       color: "default",    step: 0 },

    // --- OCR ---
    OCR_IN_PROGRESS:                { category: "in_progress", label: "识别中",       color: "processing", step: 0, pollingMessage: "正在识别账单..." },
    OCR_COMPLETED:                  { category: "completed",   label: "识别完成",     color: "cyan",       step: 1 },
    OCR_FAILED:                     { category: "failed",      label: "识别失败",     color: "error",      step: 0 },

    // --- 制表校验 (Parser) ---
    STRUCTURE_IN_PROGRESS:          { category: "in_progress", label: "制表校验中",   color: "processing", step: 1, pollingMessage: "正在制表校验..." },
    STRUCTURE_COMPLETED:            { category: "completed",   label: "制表校验完成", color: "cyan",       step: 2 },
    STRUCTURE_FAILED:               { category: "failed",      label: "制表校验失败", color: "error",      step: 1 },
    STRUCTURE_BLOCKED:              { category: "blocked",     label: "制表校验受阻", color: "warning",    step: 1 },

    // --- 分摊航班 (Allocation) ---
    ALLOCATION_IN_PROGRESS:         { category: "in_progress", label: "分摊航班中",   color: "processing", step: 2, pollingMessage: "正在分摊航班..." },
    ALLOCATION_COMPLETED:           { category: "completed",   label: "分摊航班完成", color: "cyan",       step: 3 },
    ALLOCATION_FAILED:              { category: "failed",      label: "分摊航班失败", color: "error",      step: 2 },
    ALLOCATION_BLOCKED:             { category: "blocked",     label: "分摊航班受阻", color: "warning",    step: 2 },

    // --- 验证航班 (Validation) ---
    VALIDATION_IN_PROGRESS:         { category: "in_progress", label: "验证航班中",   color: "processing", step: 3, pollingMessage: "正在验证航班..." },
    VALIDATION_COMPLETED:           { category: "completed",   label: "待审核",       color: "warning",    step: 4, validationAvailable: true },

    // --- 整批核查 (Batch Validation) ---
    BATCH_VALIDATION_IN_PROGRESS:   { category: "in_progress", label: "整批核查中",   color: "processing", step: 4, pollingMessage: "正在整批核查..." },
    BATCH_VALIDATION_COMPLETED:     { category: "completed",   label: "整批核查完成", color: "cyan",       step: 4, validationAvailable: true },
    BATCH_VALIDATION_FAILED:        { category: "failed",      label: "整批核查失败", color: "error",      step: 4, validationAvailable: true },

    // --- 终态 ---
    CONFIRMED:                      { category: "terminal",    label: "已审核",       color: "success",    step: 5, validationAvailable: true },
    REJECTED:                       { category: "terminal",    label: "已拒绝",       color: "error",      step: 5, validationAvailable: true },
    VOIDED:                         { category: "terminal",    label: "已作废",       color: "default",    step: 5, validationAvailable: true },
};

// =============================================================================
// 从注册表自动派生的常量（零维护成本）
// =============================================================================
const _entries = Object.entries(IDS_STATUS_REGISTRY);
const _byCategory = (cat) => _entries.filter(([, c]) => c.category === cat).map(([s]) => s);

/** 所有已知状态值枚举（兼容旧 INVOICE_DATA_SOURCE_STATUS 用法） */
export const INVOICE_DATA_SOURCE_STATUS = Object.fromEntries(
    _entries.map(([s]) => [s, s]),
);

/** 按分类归组 */
export const IDS_COMPLETED_STATUSES   = [..._byCategory("completed"), ..._byCategory("terminal")];
export const IDS_FAILED_STATUSES      = _byCategory("failed");
export const IDS_IN_PROGRESS_STATUSES = _byCategory("in_progress");
export const IDS_BLOCKED_STATUSES     = _byCategory("blocked");

/** 轮询提示文案 */
export const IDS_POLLING_MESSAGES = Object.fromEntries(
    _entries.filter(([, c]) => c.pollingMessage).map(([s, c]) => [s, c.pollingMessage]),
);

/** 验证结果可用的状态集 */
export const IDS_VALIDATION_AVAILABLE_STATUSES = _entries
    .filter(([, c]) => c.validationAvailable)
    .map(([s]) => s);

/** 标签映射 */
export const INVOICE_DATA_SOURCE_STATUS_LABELS = Object.fromEntries(
    _entries.map(([s, c]) => [s, c.label]),
);

/** 颜色映射 */
export const INVOICE_DATA_SOURCE_STATUS_COLORS = Object.fromEntries(
    _entries.map(([s, c]) => [s, c.color]),
);

/** { status: { label, color } } 格式（供 Tag/Badge 组件直接使用） */
export const IDS_STATUS_DISPLAY_CONFIG = Object.fromEntries(
    _entries.map(([s, c]) => [s, { label: c.label, color: c.color }]),
);

/** 步骤条映射 */
export const STATUS_STEP_MAP = Object.fromEntries(
    _entries.map(([s, c]) => [s, c.step]),
);

/** 获取当前步骤 */
export const getCurrentStep = (status) => STATUS_STEP_MAP[status] || 0;

/** 所有已知状态键列表（供筛选器等使用） */
export const IDS_ALL_STATUSES = _entries.map(([s]) => s);

// =============================================================================
// 语义化查询工具（消除组件中的硬编码状态数组）
// =============================================================================

/**
 * 判断状态是否为「执行中」—— 任何 Agent 正在跑
 * 用途：禁用操作按钮、显示停止按钮等
 */
export const idsIsProcessing = (status) =>
    IDS_STATUS_REGISTRY[status]?.category === "in_progress";

/**
 * 判断状态是否为终态。
 * 用途：进入只读模式、停止轮询、隐藏重试操作等。
 */
export const idsIsTerminal = (status) =>
    IDS_STATUS_REGISTRY[status]?.category === "terminal";

/**
 * 判断状态是否已「至少到达」给定 step 阈值
 * 用于：「结构化完成后才能查看工作台」「分摊完成后才能重试验证」等判断
 *
 * Pipeline step 定义：
 *   0=初始/OCR  1=OCR完成/结构化  2=结构化完成/分摊  3=分摊完成/验证  4=验证完成/批次验证  5=终态
 *
 * 注意：failed/blocked 的 step 代表它「停在哪」，而非「已完成哪」
 * 因此 STRUCTURE_FAILED (step=1) 表示未到达 step=2（结构化完成），符合「未到达结构化完成」的语义
 */
export const idsHasReachedStep = (status, minStep) => {
    const cfg = IDS_STATUS_REGISTRY[status];
    if (!cfg) return false;
    // failed/blocked 状态视为「未完成该阶段」
    if (cfg.category === "failed" || cfg.category === "blocked") return false;
    return cfg.step >= minStep;
};

/**
 * 默认筛选状态列表（排除 REJECTED）
 * 用于账单数据源列表页的默认筛选条件
 */
export const IDS_DEFAULT_FILTER_STATUSES = _entries
    .filter(([s]) => s !== "REJECTED")
    .map(([s]) => s);

// --- 分阶段枚举（向后兼容，旧组件仍可 import）---
export const PARSING_STATUS = {
    RECEIVED: "RECEIVED",
    OCR_IN_PROGRESS: "OCR_IN_PROGRESS",
    OCR_COMPLETED: "OCR_COMPLETED",
    OCR_FAILED: "OCR_FAILED",
};

export const STRUCTURE_STATUS = {
    NOT_STARTED: "NOT_STARTED",
    STRUCTURE_IN_PROGRESS: "STRUCTURE_IN_PROGRESS",
    STRUCTURE_COMPLETED: "STRUCTURE_COMPLETED",
    STRUCTURE_FAILED: "STRUCTURE_FAILED",
};

export const ALLOCATION_STATUS = {
    NOT_STARTED: "NOT_STARTED",
    ALLOCATION_IN_PROGRESS: "ALLOCATION_IN_PROGRESS",
    ALLOCATION_COMPLETED: "ALLOCATION_COMPLETED",
    ALLOCATION_FAILED: "ALLOCATION_FAILED",
};

// 旧标签/颜色常量 — 从注册表派生（不再手动维护）
export const PARSING_STATUS_LABELS = Object.fromEntries(
    Object.keys(PARSING_STATUS).map((s) => [s, IDS_STATUS_REGISTRY[s]?.label || s]),
);
export const PARSING_STATUS_COLORS = Object.fromEntries(
    Object.keys(PARSING_STATUS).map((s) => [s, IDS_STATUS_REGISTRY[s]?.color || "default"]),
);
export const STRUCTURE_STATUS_LABELS = {
    NOT_STARTED: "未开始",
    ...Object.fromEntries(
        Object.keys(STRUCTURE_STATUS).filter((s) => s !== "NOT_STARTED")
            .map((s) => [s, IDS_STATUS_REGISTRY[s]?.label || s]),
    ),
};
export const STRUCTURE_STATUS_COLORS = {
    NOT_STARTED: "default",
    ...Object.fromEntries(
        Object.keys(STRUCTURE_STATUS).filter((s) => s !== "NOT_STARTED")
            .map((s) => [s, IDS_STATUS_REGISTRY[s]?.color || "default"]),
    ),
};
export const ALLOCATION_STATUS_LABELS = {
    NOT_STARTED: "未开始",
    ...Object.fromEntries(
        Object.keys(ALLOCATION_STATUS).filter((s) => s !== "NOT_STARTED")
            .map((s) => [s, IDS_STATUS_REGISTRY[s]?.label || s]),
    ),
};
export const ALLOCATION_STATUS_COLORS = {
    NOT_STARTED: "default",
    ...Object.fromEntries(
        Object.keys(ALLOCATION_STATUS).filter((s) => s !== "NOT_STARTED")
            .map((s) => [s, IDS_STATUS_REGISTRY[s]?.color || "default"]),
    ),
};

// 来源类型枚举
// 用于标识 InvoiceDataSource 数据的创建入口，与后端 SOURCE_TYPES 保持一致
export const SOURCE_TYPE = {
    // 主要来源类型（筛选器可见）
    EMAIL: "EMAIL",                       // 邮件导入 - 通过邮件解析OCR自动导入
    UPLOAD: "UPLOAD",                     // 通用上传 - 旧数据兼容
    INVOICE_UPLOAD: "INVOICE_UPLOAD",     // 账单上传 - 前端批量上传账单文件
    OFFLINE: "OFFLINE",                   // 线下数据 - Excel leg-cost 导入
    API: "API",                           // API导入 - 第三方系统对接
    // 手工录入类型
    MANUAL_ENTRY: "MANUAL_ENTRY",         // 手工录入 - 费用分摊录入、航段费用手工录入
    // 系统内部流程类型
    AP_RECONCILIATION: "AP_RECONCILIATION", // AP对账 - AP审核确认后自动创建
    // 合作方共享类型
    PARTNER_BILLING: "PARTNER_BILLING",     // 合作方账单 - 原始账单共享给运营合作方
    // Agent 交单类型（结算 AP Phase 1）
    AGENT_UPLOAD: "AGENT_UPLOAD",           // Agent 交单 - 账单经文档中心上传后由 Agent 交单登记
};

// 来源类型标签
export const SOURCE_TYPE_LABELS = {
    [SOURCE_TYPE.EMAIL]: "邮件导入",
    [SOURCE_TYPE.UPLOAD]: "手动上传",
    [SOURCE_TYPE.INVOICE_UPLOAD]: "账单上传",
    [SOURCE_TYPE.OFFLINE]: "线下数据",
    [SOURCE_TYPE.API]: "API导入",
    [SOURCE_TYPE.MANUAL_ENTRY]: "手工录入",
    [SOURCE_TYPE.AP_RECONCILIATION]: "AP对账",
    [SOURCE_TYPE.PARTNER_BILLING]: "合作方账单",
    [SOURCE_TYPE.AGENT_UPLOAD]: "Agent 交单",
};

// 来源类型颜色
export const SOURCE_TYPE_COLORS = {
    [SOURCE_TYPE.EMAIL]: "green",
    [SOURCE_TYPE.UPLOAD]: "blue",
    [SOURCE_TYPE.INVOICE_UPLOAD]: "geekblue",
    [SOURCE_TYPE.OFFLINE]: "orange",
    [SOURCE_TYPE.API]: "purple",
    [SOURCE_TYPE.MANUAL_ENTRY]: "cyan",
    [SOURCE_TYPE.AP_RECONCILIATION]: "magenta",
    [SOURCE_TYPE.PARTNER_BILLING]: "gold",
    [SOURCE_TYPE.AGENT_UPLOAD]: "volcano",
};

// 标准费用类型枚举（严格对齐后端 CostType 枚举，共21项）
// 注意：此字段是用户在上传账单/创建 IDS 时对文件内容的「预分类」，
// 代表该批账单的主要费用大类，而非每一行 line_item 的精确费用类型。
// 一张账单可能同时包含多种费用（如 Ground Handling + Fuel），
// 精确分类在 AI 解析后由 line_item 的 product_code 决定。
export const STANDARD_COST_TYPE = {
    GROUND_HANDLING: "GROUND_HANDLING",
    FUEL: "FUEL",
    LANDING_PARKING: "LANDING_PARKING",
    CATERING: "CATERING",
    TRANSPORTATION: "TRANSPORTATION",
    DEICING: "DEICING",
    NAVIGATION: "NAVIGATION",
    POSITIONING: "POSITIONING",
    LAUNDRY: "LAUNDRY",
    ACCOMMODATION: "ACCOMMODATION",
    DISINFECTION: "DISINFECTION",
    FLIGHT_SERVICE_CHARGE: "FLIGHT_SERVICE_CHARGE",
    ULD: "ULD",
    TRAFFIC_RIGHTS_PERMIT: "TRAFFIC_RIGHTS_PERMIT",
    SITA: "SITA",
    COMMISSION: "COMMISSION",
    ACMI: "ACMI",
    TRAFFIC_RIGHTS: "TRAFFIC_RIGHTS",
    OTHERS_FEE: "OTHERS_FEE",
    CARGO_HANDLING: "CARGO_HANDLING",
    BH: "BH", // Block Hours - 飞行小时（参照旧系统）
    FH: "FH", // Flight Hours - 飞行时间
    TRUCKING_FEE: "TRUCKING_FEE", // 卡车运输费(2026-05新增,独立于 TRANSPORTATION)
};

// 标准费用类型标签 (English labels for user display)
// 用于预分类的显示，列标题应标注为「费用类型（预分类）」
export const STANDARD_COST_TYPE_LABELS = {
    [STANDARD_COST_TYPE.GROUND_HANDLING]: "Ground Handling",
    [STANDARD_COST_TYPE.FUEL]: "Fuel",
    [STANDARD_COST_TYPE.LANDING_PARKING]: "Landing & Parking",
    [STANDARD_COST_TYPE.CATERING]: "Catering",
    [STANDARD_COST_TYPE.TRANSPORTATION]: "Transportation",
    [STANDARD_COST_TYPE.DEICING]: "De-icing",
    [STANDARD_COST_TYPE.NAVIGATION]: "Navigation",
    [STANDARD_COST_TYPE.POSITIONING]: "Positioning",
    [STANDARD_COST_TYPE.LAUNDRY]: "Laundry",
    [STANDARD_COST_TYPE.ACCOMMODATION]: "Accommodation",
    [STANDARD_COST_TYPE.DISINFECTION]: "Disinfection",
    [STANDARD_COST_TYPE.FLIGHT_SERVICE_CHARGE]: "Flight Service Charge",
    [STANDARD_COST_TYPE.BH]: "Block Hours (BH)",
    [STANDARD_COST_TYPE.FH]: "Flight Hours (FH)",
    [STANDARD_COST_TYPE.ULD]: "ULD",
    [STANDARD_COST_TYPE.TRAFFIC_RIGHTS_PERMIT]: "Traffic Rights Permit",
    [STANDARD_COST_TYPE.SITA]: "SITA",
    [STANDARD_COST_TYPE.COMMISSION]: "Commission",
    [STANDARD_COST_TYPE.ACMI]: "ACMI",
    [STANDARD_COST_TYPE.TRAFFIC_RIGHTS]: "Traffic Rights",
    [STANDARD_COST_TYPE.OTHERS_FEE]: "Others",
    [STANDARD_COST_TYPE.CARGO_HANDLING]: "Cargo Handling",
    [STANDARD_COST_TYPE.TRUCKING_FEE]: "Trucking Fee",
};

// 兼容后端小写存储的显示函数
export const getStandardCostTypeLabel = (costType) => {
    if (!costType) return "-";

    // 先尝试大写匹配
    const upperKey = costType.toUpperCase();
    const foundLabel = STANDARD_COST_TYPE_LABELS[upperKey];
    if (foundLabel) {
        return foundLabel;
    }

    // 如果大写没匹配，尝试直接匹配
    return STANDARD_COST_TYPE_LABELS[costType] || costType || "-";
};

// 标准费用类型颜色
export const STANDARD_COST_TYPE_COLORS = {
    [STANDARD_COST_TYPE.GROUND_HANDLING]: "blue",
    [STANDARD_COST_TYPE.FUEL]: "red",
    [STANDARD_COST_TYPE.LANDING_PARKING]: "purple",
    [STANDARD_COST_TYPE.CATERING]: "orange",
    [STANDARD_COST_TYPE.TRANSPORTATION]: "geekblue",
    [STANDARD_COST_TYPE.DEICING]: "cyan",
    [STANDARD_COST_TYPE.NAVIGATION]: "green",
    [STANDARD_COST_TYPE.POSITIONING]: "magenta",
    [STANDARD_COST_TYPE.LAUNDRY]: "volcano",
    [STANDARD_COST_TYPE.ACCOMMODATION]: "gold",
    [STANDARD_COST_TYPE.DISINFECTION]: "lime",
    [STANDARD_COST_TYPE.FLIGHT_SERVICE_CHARGE]: "purple",
    [STANDARD_COST_TYPE.ULD]: "blue",
    [STANDARD_COST_TYPE.TRAFFIC_RIGHTS_PERMIT]: "geekblue",
    [STANDARD_COST_TYPE.SITA]: "cyan",
    [STANDARD_COST_TYPE.COMMISSION]: "orange",
    [STANDARD_COST_TYPE.ACMI]: "magenta",
    [STANDARD_COST_TYPE.TRAFFIC_RIGHTS]: "green",
    [STANDARD_COST_TYPE.OTHERS_FEE]: "default",
    [STANDARD_COST_TYPE.CARGO_HANDLING]: "volcano",
    [STANDARD_COST_TYPE.TRUCKING_FEE]: "gold",
};

// 分摊类型枚举
export const ALLOCATION_TYPE = {
    DIRECT: "DIRECT",
    RULE_BASED: "RULE_BASED",
};

// 分摊类型标签
export const ALLOCATION_TYPE_LABELS = {
    [ALLOCATION_TYPE.DIRECT]: "直接",
    [ALLOCATION_TYPE.RULE_BASED]: "规则",
};

// 分摊类型颜色
export const ALLOCATION_TYPE_COLORS = {
    [ALLOCATION_TYPE.DIRECT]: "blue",
    [ALLOCATION_TYPE.RULE_BASED]: "green",
};

// 分摊类型选项（用于选择器）
export const ALLOCATION_TYPE_OPTIONS = [
    {
        value: ALLOCATION_TYPE.DIRECT,
        label: ALLOCATION_TYPE_LABELS[ALLOCATION_TYPE.DIRECT],
    },
    {
        value: ALLOCATION_TYPE.RULE_BASED,
        label: ALLOCATION_TYPE_LABELS[ALLOCATION_TYPE.RULE_BASED],
    },
];

// 分摊规则枚举
export const ALLOCATION_RULE = {
    BY_FLIGHT_COUNT: "BY_FLIGHT_COUNT",
    BY_FLIGHT_HOURS: "BY_FLIGHT_HOURS",
    BY_AIRCRAFT_TYPE: "BY_AIRCRAFT_TYPE",
    BY_FUEL_QUANTITY: "BY_FUEL_QUANTITY",
    MANUAL: "MANUAL",
};

// 分摊规则标签
export const ALLOCATION_RULE_LABELS = {
    [ALLOCATION_RULE.BY_FLIGHT_COUNT]: "按航班数量平均分摊",
    [ALLOCATION_RULE.BY_FLIGHT_HOURS]: "按飞行小时分摊",
    [ALLOCATION_RULE.BY_AIRCRAFT_TYPE]: "按机型权重分摊",
    [ALLOCATION_RULE.BY_FUEL_QUANTITY]: "按当前账单加油量分摊",
    [ALLOCATION_RULE.MANUAL]: "手动指定比例",
};

// 分摊规则颜色
export const ALLOCATION_RULE_COLORS = {
    [ALLOCATION_RULE.BY_FLIGHT_COUNT]: "blue",
    [ALLOCATION_RULE.BY_FLIGHT_HOURS]: "green",
    [ALLOCATION_RULE.BY_AIRCRAFT_TYPE]: "orange",
    [ALLOCATION_RULE.BY_FUEL_QUANTITY]: "geekblue",
    [ALLOCATION_RULE.MANUAL]: "purple",
};

// 分摊规则选项（用于选择器）
export const ALLOCATION_RULE_OPTIONS = Object.values(ALLOCATION_RULE).map((key) => ({
    value: key,
    label: ALLOCATION_RULE_LABELS[key],
}));

// 标准费用类型选项（用于选择器），统一从常量生成，值为后端存储的uppercase字符串
export const STANDARD_COST_TYPE_OPTIONS = Object.values(STANDARD_COST_TYPE).map((key) => ({
    value: key,
    label: STANDARD_COST_TYPE_LABELS[key],
}));

// 货币符号映射 — 业务通用常量（从 ReconciliationWorkspace 提取，供多组件复用）
export const CURRENCY_SYMBOLS = {
    USD: "$",
    EUR: "€",
    HKD: "HK$",
    RMB: "¥",
    CNY: "¥",
    GBP: "£",
    JPY: "¥",
    KRW: "₩",
    SGD: "S$",
    AUD: "A$",
    CAD: "C$",
    CHF: "CHF",
    NZD: "NZ$",
    AED: "AED",
};

// RMB 只用于历史展示，写入结算币种必须用 ISO 4217 代码。
export const WRITABLE_CURRENCY_CODES = Object.keys(CURRENCY_SYMBOLS).filter((code) => code !== "RMB");

// 对账工作区视图过滤器
export const RECONCILIATION_VIEW_FILTER = {
    ALL: "all",
    PROBLEMS_ONLY: "problems_only",
    USER_MODIFIED: "user_modified",
    FLIGHT_UNMATCHED: "flight_unmatched",
    FLIGHT_MATCHED: "flight_matched",
};

// 对账工作区视图过滤器标签
export const RECONCILIATION_VIEW_FILTER_LABELS = {
    [RECONCILIATION_VIEW_FILTER.ALL]: "全部",
    [RECONCILIATION_VIEW_FILTER.PROBLEMS_ONLY]: "仅问题项目",
    [RECONCILIATION_VIEW_FILTER.USER_MODIFIED]: "仅修改项目",
    [RECONCILIATION_VIEW_FILTER.FLIGHT_UNMATCHED]: "航班未匹配",
    [RECONCILIATION_VIEW_FILTER.FLIGHT_MATCHED]: "航班已匹配",
};

// 向后兼容的工作台视图过滤器
export const WORKBENCH_VIEW_FILTER = RECONCILIATION_VIEW_FILTER;
export const WORKBENCH_VIEW_FILTER_LABELS = RECONCILIATION_VIEW_FILTER_LABELS;
